import _ from 'lodash'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import { BarcodeDetectionResult } from './barcode-result'
import { Point, Polygon } from 'shared-libs/helpers/vision/polygon-utils'
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser'
import { IBarcodeDetector, IBarcodeDetectorOptions } from './barcode-detector'

export class ZxingBarcodeDetector implements IBarcodeDetector {
  private static readonly ALLOWED_FORMATS = [
    BarcodeFormat.AZTEC,
    BarcodeFormat.CODABAR,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_93,
    BarcodeFormat.CODE_128,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.EAN_8,
    BarcodeFormat.EAN_13,
    BarcodeFormat.ITF,
    BarcodeFormat.MAXICODE,
    BarcodeFormat.PDF_417,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.RSS_14,
    BarcodeFormat.RSS_EXPANDED,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.UPC_EAN_EXTENSION,
  ]

  private codeReader: BrowserMultiFormatReader
  private scannerControls?: IScannerControls

  constructor(private options?: IBarcodeDetectorOptions) {
    const zxingOptions = options?.zxing || {}

    const formats =
      zxingOptions.formats?.map(parseFormat).filter((f): f is BarcodeFormat => f != null) ||
      ZxingBarcodeDetector.ALLOWED_FORMATS

    const tryHarder = zxingOptions.tryHarder ?? true

    const hints = new Map<DecodeHintType, boolean | BarcodeFormat[]>([
      [DecodeHintType.TRY_HARDER, tryHarder],
      [DecodeHintType.POSSIBLE_FORMATS, formats],
    ])
    this.codeReader = new BrowserMultiFormatReader(hints)
  }

  async detect(
    videoElement: HTMLVideoElement,
    onResult: (results: BarcodeDetectionResult[]) => void,
    onError: (error: unknown) => void
  ): Promise<void> {
    try {
      await this.codeReader.decodeFromConstraints(
        {
          video: {
            facingMode: 'environment',
          },
        },
        videoElement,
        (result, error, controls) => {
          this.scannerControls = controls

          if (result) {
            const points = result.getResultPoints().map((point) => ({
              x: point.getX(),
              y: point.getY(),
            }))
            const boundingBox = this.computeBoundingBox(points)

            // ZXing only produces one result, so mark it as the primary result.
            const isPrimary = true
            // if ZXing detects a barcode, then the user doesn't need corrective
            // feedback since their positioning was likely sufficient.
            const isNear = true
            const isSteady = true

            const detectionResult: BarcodeDetectionResult = {
              barcode: result.getText(),
              cornerPoints: boundingBox,
              isPrimary,
              isNear,
              isSteady,
            }
            onResult([detectionResult])
          }
        }
      )
    } catch (error) {
      onError(error instanceof Error ? error : new Error(String(error)))
    }
  }

  stop(): void {
    this.scannerControls?.stop()
    BrowserMultiFormatReader.releaseAllStreams()
  }

  private FAKE_BARCODE_HEIGHT_PX = 10

  private computeBoundingBox(resultPoints: Point[]): Polygon {
    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity

    for (const point of resultPoints) {
      if (point.x < minX) minX = point.x
      if (point.x > maxX) maxX = point.x
      if (point.y < minY) minY = point.y
      if (point.y > maxY) maxY = point.y
    }

    // ensure the bounding box has a minimum thickness so it at least looks like
    // a scan line, since zxing doesn't provide a complete bounding box.
    const width = Math.max(maxX - minX, this.FAKE_BARCODE_HEIGHT_PX)
    const height = Math.max(maxY - minY, this.FAKE_BARCODE_HEIGHT_PX)

    return [
      { x: minX, y: minY },
      { x: minX + width, y: minY },
      { x: minX + width, y: minY + height },
      { x: minX, y: minY + height },
    ]
  }
}

function isBarcodeFormatKey(format: string): format is keyof typeof BarcodeFormat {
  return format in BarcodeFormat
}

function parseFormat(format: string | number): BarcodeFormat | undefined {
  if (typeof format === 'string') {
    return isBarcodeFormatKey(format) ? BarcodeFormat[format] : undefined
  } else if (typeof format === 'number' && BarcodeFormat[format]) {
    return format
  }
}
